import { EntitlementDenialReason, FeatureKey } from '@/api/entitlements';
import { ReadinessStepKey } from '@/api/readiness';
import type { InstrumentSource } from '@/types/report-payload';

/**
 * Which route the "Adicionar instrumento" menu was answered with.
 *
 * Wider than the payload's `InstrumentSource` on purpose: a pasted table is
 * stored as `free_mode` like a hand-described one, and collapsing the two here
 * would hide exactly the thing this event exists to measure — whether people
 * find the clipboard route at all.
 */
export type AddInstrumentAnalyticsSource = InstrumentSource | 'clipboard';

/**
 * Where a suggestion's content came from: a SATEPSI row, the naming model
 * alone, or nothing at all. Deliberately not the instrument's name — no
 * property here may carry something a model wrote or a user typed.
 */
export type InstrumentSuggestionAnalyticsSource = 'satepsi' | 'model' | 'none';

/**
 * The product-analytics event registry.
 *
 * This file is the only place an event name may be introduced. `capture()` in
 * `@/lib/posthog` accepts nothing outside this map, which is what makes the
 * privacy rules in docs/analytics.md enforceable by the compiler rather than by
 * memory: there is no way to pass an ad-hoc property bag through it.
 *
 * Rules that apply to every entry below:
 *
 * - No PII. No email, name, phone, CRP, birth date, city (R1).
 * - Nothing the user typed. No form values, no search terms, no report or
 *   anamnesis text, no diagnoses, no test answers (R2).
 * - No entity IDs other than the psychologist UUID — no patient_id, no
 *   report_id, no template_id. They are opaque, but they let this data be
 *   joined back to clinical records. Describe the situation with counts,
 *   booleans and enums instead (R3).
 * - Property values are booleans, numbers, or string *literal unions*. Never
 *   `string`, never free text (R5).
 *
 * A unit test asserts no property name here resembles a PII field.
 */

/** Where a flow was entered from — useful for funnels, carries no user data. */
export type AnalyticsSource =
    | 'patients_page'
    | 'patient_detail'
    | 'readiness_checklist'
    | 'report_wizard'
    | 'dashboard'
    | 'welcome'
    | 'sidebar';

/** Built-in wizard step ids, mirroring SECTIONS in @/components/wizard/constants. */
const BUILT_IN_REPORT_STEP_KEYS = [
    'patient',
    'anamnesis',
    'demand_description',
    'procedures',
    'behavior_observations',
    'clinical_history',
    'tests',
    'diagnosis',
    'recommendations',
] as const;

export type ReportStepKey = (typeof BUILT_IN_REPORT_STEP_KEYS)[number] | 'custom_section';

/**
 * The only way to obtain a `ReportStepKey` from a wizard step.
 *
 * Custom sections carry a per-report random id (`custom-<timestamp>-<random>`,
 * see `createCustomSectionId` in @/lib/report-sections). Sending that as
 * `step_key` would put an unbounded, report-scoped identifier into the funnel —
 * it breaks grouping and it is exactly the kind of id R3 forbids. They all
 * collapse into one bucket instead.
 *
 * An id we do not recognise returns null so the caller skips the event, rather
 * than being cast into the union and shipped raw.
 */
export const toReportStepKey = (
    stepId: string | undefined,
    isCustom: boolean | undefined,
): ReportStepKey | null => {
    if (isCustom) return 'custom_section';
    if (!stepId) return null;

    return (BUILT_IN_REPORT_STEP_KEYS as readonly string[]).includes(stepId)
        ? (stepId as ReportStepKey)
        : null;
};

/**
 * How the user arrived at a wizard step.
 *
 * The step-viewed effect in CreateReport() runs on a state change and cannot
 * see its own cause, so the caller records the intent and the effect consumes
 * it. Without this, deliberately jumping via the sidebar and following the
 * laudo along in the preview produce identical events — and those are two very
 * different ways of working.
 */
export type ReportStepViewVia =
    /** First step rendered after the editor loads. */
    | 'initial'
    /** The step list on the left. */
    | 'sidebar'
    /** Clicking the section inside the rendered laudo. */
    | 'preview'
    /** An "ir para seção" card on an AI data-source list. */
    | 'shortcut_card'
    /** The "Ir para" submenu in the report canvas's right-click menu. */
    | 'page_menu'
    /** A custom section was just created and selected. */
    | 'new_section'
    /** Reorder, delete or an index clamp — a move, not a navigation intent. */
    | 'programmatic';

/**
 * Every intent-only control in the report editor's chrome.
 *
 * These share one event because the useful view is a single trend broken down
 * by `action`: a new button then appears in the existing insights instead of
 * silently missing from them. Anything whose properties differ gets its own
 * event below rather than a pile of optional fields here.
 */
export type ReportEditorAction =
    // Header
    | 'back'
    | 'save'
    | 'customize'
    | 'template_save'
    | 'template_list'
    | 'report_import'
    | 'conclude'
    | 'export'
    | 'comments_all'
    | 'comments_new'
    // Steps sidebar
    | 'custom_section_add'
    | 'custom_section_remove'
    | 'cover_open'
    | 'step_marked_done'
    | 'step_marked_todo';

/** The three export paths carry very different intent. */
export type ReportExportTrigger = 'header_button' | 'completion_modal' | 'crp_retry';

/** Where a plan or quota wall was hit. */
export type BlockedSurface =
    | 'report_editor'
    | 'report_cover'
    | 'report_customize'
    | 'report_templates'
    | 'anamnesis_modal';

/**
 * Why a report action failed, bucketed from the HTTP status alone.
 *
 * Never the error message: API messages can quote back what the user typed.
 */
export type ReportFailureKind =
    | 'permission'
    | 'quota'
    | 'server'
    | 'network'
    | 'validation'
    | 'unknown';

/** Which AI entry point was used — in practice these behave as different tools. */
export type ReportAiSurface = 'section_card' | 'inline_editor' | 'test_block';

export type PlanKey = 'TRIAL' | 'BASIC' | 'INTERMEDIATE' | 'PRO';

/**
 * Frontend-owned events only: navigation and intent.
 *
 * Lifecycle milestones (patient created, report created, report completed,
 * trial started, subscription active) are emitted by the API so they survive
 * ad blockers and are correct across devices. Never add one of those here —
 * the same event name on both sides double-counts. See docs/analytics.md.
 */
export type AnalyticsEventMap = {
    /** Route-pattern pageview. Emitted only by PageTitle(); never call directly. */
    $pageview: {
        route: string;
    };

    add_patient_modal_opened: {
        source: AnalyticsSource;
    };
    /** Which arm of the add-patient fork the user chose. */
    add_patient_method_chosen: {
        method: 'manual' | 'intake_link';
        source: AnalyticsSource;
    };
    /**
     * Fires on submit, before the request resolves. The API owns the
     * `patient_created` success event; the gap between the two is where
     * validation failures and plan-cap rejections hide.
     */
    patient_form_submitted: {
        mode: 'create' | 'edit';
    };

    report_wizard_step_viewed: {
        step_key: ReportStepKey;
        step_index: number;
        via: ReportStepViewVia;
    };
    template_prompt_shown: {
        template_count: number;
    };
    template_selected: {
        template_source: 'system' | 'custom';
    };
    template_prompt_dismissed: Record<string, never>;

    /**
     * A control in the editor chrome was pressed. Intent only — whether the
     * action then succeeded is a separate event, or the API's.
     */
    report_editor_action_clicked: {
        action: ReportEditorAction;
        is_read_only: boolean;
    };
    /**
     * An "Adicionar instrumento" entry point was pressed. Intent only — the
     * selection itself may still be cancelled in the modal. Broken down by
     * surface so the funnel shows which entry users actually find.
     */
    report_instrument_add_clicked: {
        /** `empty_state` is the laudo's own empty instrument section, on the
         *  canvas. `catalog_empty_state` is the dead end *inside* the Satepsi
         *  picker — a search that returned nothing — which is a different
         *  funnel: it counts the times the catalog failed the user, and how
         *  often the way out of it gets taken. */
        surface:
            | 'panel'
            | 'empty_state'
            | 'catalog_empty_state'
            | 'table_placeholder'
            | 'context_menu';
        /** Which of the three routes was chosen. Present on the surfaces that
         *  offer the menu (`panel`, `empty_state`); absent on
         *  `table_placeholder` and `context_menu`, which still go straight to
         *  the Satepsi catalog. */
        instrument_source?: AddInstrumentAnalyticsSource;
    };
    /**
     * A suggestion for a pasted table came back. Fired on the *response*, never
     * on the request: StrictMode runs the effect twice in dev, and counting
     * requests would double every number here.
     *
     * `outcome: 'empty'` is the healthy majority — a home-made scoring sheet
     * has no instrument behind it. What this measures against
     * `instrument_suggestion_applied` is whether the suggestion is worth its
     * tokens.
     */
    instrument_suggestion_returned: {
        outcome: 'suggested' | 'empty' | 'failed';
        suggestion_source: InstrumentSuggestionAnalyticsSource;
        /** Whether the SATEPSI citation came with it — the field nobody types. */
        filled_reference: boolean;
    };
    /** The suggestion line was clicked, so the fields were written. */
    instrument_suggestion_applied: {
        suggestion_source: InstrumentSuggestionAnalyticsSource;
    };
    /**
     * The name was typed by hand while a suggestion was on screen, which
     * withdraws it. There is no dismiss control to click: this counts the
     * psychologist writing the answer themselves, and its distance from
     * `applied` is the suggestion's accuracy.
     */
    instrument_suggestion_dismissed: {
        suggestion_source: InstrumentSuggestionAnalyticsSource;
    };
    /**
     * An export request actually left the client. Deliberately distinct from
     * `report_editor_action_clicked: 'export'`, which is only the button press:
     * the gap between the two is exports we refuse before ever asking the API.
     */
    report_export_clicked: {
        trigger: ReportExportTrigger;
    };
    /**
     * The user asked to finish and our own validation refused. The API owns
     * `report_completed`; the distance between this and that is where laudos
     * stall a few sections short of done.
     */
    report_conclude_blocked: {
        incomplete_step_count: number;
    };
    /**
     * The click. The API owns `ai_generation_used`, so the difference between
     * the two counts generations that failed, were cancelled, or were refused.
     */
    report_ai_generation_requested: {
        step_key: ReportStepKey;
        surface: ReportAiSurface;
        mode: 'create' | 'improve';
    };
    /**
     * A report action failed after the user committed to it. Someone whose
     * first laudo would not save looks exactly like someone who lost interest,
     * unless we record this. `autosave` fires once per failure streak, not per
     * tick, so a payload the API keeps refusing counts as one failure.
     */
    report_action_failed: {
        action: 'save' | 'autosave' | 'export' | 'conclude' | 'ai_generate';
        failure_kind: ReportFailureKind;
    };

    /**
     * A plan or quota gate refused something the user deliberately reached for.
     *
     * Every one of these ends in a toast and nothing else, which makes it the
     * likeliest silent explanation for an account that signs up, tries once and
     * never returns. Fire it from the handler, never from a render-time
     * disabled state — that would emit on every mount.
     */
    feature_blocked: {
        feature_key: FeatureKey;
        denial_reason: EntitlementDenialReason;
        surface: BlockedSurface;
    };

    /** The click. The API owns `trial_started`. Reaching the screen at all is
     * already covered by the `/welcome` pageview. */
    welcome_trial_cta_clicked: Record<string, never>;
    readiness_step_cta_clicked: {
        step_key: ReadinessStepKey;
    };

    /** The click. The API owns the subscription lifecycle. */
    plan_checkout_started: {
        plan: PlanKey;
        had_subscription: boolean;
    };
};

export type AnalyticsEventName = keyof AnalyticsEventMap;

/**
 * Property names that must never appear in the registry, checked by a unit
 * test. This is a backstop for review, not a substitute for it.
 */
export const BANNED_PROPERTY_PATTERNS = [
    /mail/i,
    /name/i,
    /phone/i,
    /crp/i,
    /license/i,
    /birth/i,
    /cpf/i,
    /address/i,
    /city/i,
    /state/i,
    /instagram/i,
    /website/i,
    /avatar/i,
    /patient_id/i,
    /report_id/i,
    /assignment_id/i,
    /template_id/i,
    /survey_id/i,
    /text/i,
    /content/i,
    /title/i,
    /query/i,
    /search/i,
    /diagnosis/i,
    /score/i,
    /answer/i,
];
